import { createReducer } from '@reduxjs/toolkit';
import {
  CLEAR_EDIT_CUSTOM_STATUS,
  DELETE_REDACTION_CUSTOM_STATUS_FAILURE,
  DELETE_REDACTION_CUSTOM_STATUS_REQUEST,
  DELETE_REDACTION_CUSTOM_STATUS_SUCCESS,
  FETCH_REDACTION_STATUSES,
  FETCH_REDACTION_STATUSES_FAILURE,
  FETCH_REDACTION_STATUSES_SUCCESS,
  PERMANENTLY_DELETE_REDACTION_CUSTOM_STATUS_REQUEST,
  RESTORE_REDACTION_CUSTOM_STATUS_FAILURE,
  RESTORE_REDACTION_CUSTOM_STATUS_REQUEST,
  RESTORE_REDACTION_CUSTOM_STATUS_SUCCESS,
  REORDER_REDACTION_CUSTOM_STATUSES_FAILURE,
  REORDER_REDACTION_CUSTOM_STATUSES_REQUEST,
  REORDER_REDACTION_CUSTOM_STATUSES_SUCCESS,
  SAVE_REDACTION_CUSTOM_STATUS_FAILURE,
  SAVE_REDACTION_CUSTOM_STATUS_REQUEST,
  SAVE_REDACTION_CUSTOM_STATUS_SUCCESS,
  SAVE_REDACTION_STATUSES_FAILURE,
  SAVE_REDACTION_STATUSES_REQUEST,
  SAVE_REDACTION_STATUSES_SUCCESS,
  SET_EDIT_CUSTOM_STATUS,
  TOGGLE_STATUS_DRAWER,
} from '../actions';
import {
  FetchRedactionStatusesResponse,
  RedactionStatusesState,
  ReorderRedactionCustomStatusesRequestPayload,
  SaveRedactionStatusesRequestPayload,
  SaveRedactionStatusesSuccessPayload,
  SetEditCustomStatusPayload,
  getPartialRedactionStatusSettingsFromRecords,
  getRedactionCustomStatusesFromRecords,
  getRedactionStatusSdoIdsFromRecords,
  getRedactionStatusSettingsFromRecords,
} from '../models';
import { defaultState, namespace } from '../store';
import { normalizeRedactionStatusSettings } from '@common-modules/mediaDetails/redactionStatuses';

const fetchRedactionStatuses = (state: RedactionStatusesState) => ({
  ...state,
  isFetchingStatuses: true,
});

const fetchRedactionStatusesSuccess = (
  state: RedactionStatusesState,
  { payload }: { payload: FetchRedactionStatusesResponse }
) => {
  const records = payload.structuredDataObjects.records;

  // Suspect-empty response: this org is known to have status rows, so zero
  // records is a fault (a cluster repointed at another schema id, an org-scoping
  // hiccup, or a 200 whose `structuredDataObjects` is null — see
  // services/index.ts) rather than "this org has no statuses". Keep what we
  // already hold. Overwriting would blank `customStatuses`, turning every custom
  // `statusKey` UUID on every file into an apparent orphan and dropping its edit
  // lock — the same fail-open as the failure path, reached without a GraphQL
  // error. This is the read-side twin of the write-side seeding guard in
  // sagas/index.ts, which refuses to re-seed defaults under the same condition
  // (VE-24546).
  if (records.length === 0 && state.hasPersistedStatusRecords) {
    return { ...state, isFetchingStatuses: false };
  }

  return {
    ...state,
    isFetchingStatuses: false,
    hasLoadedStatuses: true,
    // Sticky: once an org is known to have rows, a later empty response is
    // treated as suspect rather than as "this org has no settings".
    hasPersistedStatusRecords:
      state.hasPersistedStatusRecords || records.length > 0,
    redactionStatuses: getRedactionStatusSettingsFromRecords(records),
    sdoIdsByStatus: getRedactionStatusSdoIdsFromRecords(records),
    customStatuses: getRedactionCustomStatusesFromRecords(records),
  };
};

// Deliberately does NOT set hasLoadedStatuses: a failed fetch leaves
// `customStatuses` untrustworthy, and marking it loaded would both unlock every
// custom status (see isRedactionStatusLockEdits) and make the editor settle gate
// short-circuit for the rest of the session (VE-24546).
const fetchRedactionStatusesFailure = (state: RedactionStatusesState) => ({
  ...state,
  isFetchingStatuses: false,
});

const saveRedactionStatusesRequest = (
  state: RedactionStatusesState,
  _action: { payload: SaveRedactionStatusesRequestPayload }
) => ({
  ...state,
  isSavingStatuses: true,
});

const saveRedactionStatusesSuccess = (
  state: RedactionStatusesState,
  { payload }: { payload: SaveRedactionStatusesSuccessPayload }
) => ({
  ...state,
  isSavingStatuses: false,
  // Rows now exist for this org, so a later empty fetch must not re-seed.
  hasPersistedStatusRecords:
    state.hasPersistedStatusRecords || payload.records.length > 0,
  redactionStatuses: normalizeRedactionStatusSettings({
    ...state.redactionStatuses,
    ...getPartialRedactionStatusSettingsFromRecords(payload.records),
  }),
  sdoIdsByStatus: {
    ...state.sdoIdsByStatus,
    ...getRedactionStatusSdoIdsFromRecords(payload.records),
  },
});

const saveRedactionStatusesFailure = (state: RedactionStatusesState) => ({
  ...state,
  isSavingStatuses: false,
});

const savingCustomStatus = (state: RedactionStatusesState) => ({
  ...state,
  isSavingStatuses: true,
});

// Optimistic: apply the new order now; the saga refetches to reconcile (VE-24546 #1).
const reorderStatusesRequest = (
  state: RedactionStatusesState,
  { payload }: { payload: ReorderRedactionCustomStatusesRequestPayload }
) => ({
  ...state,
  isSavingStatuses: true,
  customStatuses: payload.customStatuses,
  redactionStatuses: payload.builtInSettings
    ? normalizeRedactionStatusSettings(payload.builtInSettings)
    : state.redactionStatuses,
});

/**
 * Close the drawer; the saga refetches to reflect persisted rows.
 *
 * Only for the drawer's own save. The settings table dispatches this same action
 * for its per-row enabled / lockEditing toggles, and closing on those threw away
 * whatever the user had typed in an Add/Edit drawer opened while the toggle's
 * round trip was still in flight (VE-24546).
 */
const saveCustomStatusSuccess = (
  state: RedactionStatusesState,
  { payload }: { payload?: { closeDrawer?: boolean } }
) => ({
  ...state,
  isSavingStatuses: false,
  ...(payload?.closeDrawer && {
    isStatusDrawerOpen: false,
    selectedCustomStatus: undefined,
  }),
});

const customStatusOpFailure = (state: RedactionStatusesState) => ({
  ...state,
  isSavingStatuses: false,
});

const customStatusOpSuccess = (state: RedactionStatusesState) => ({
  ...state,
  isSavingStatuses: false,
});

const toggleStatusDrawer = (state: RedactionStatusesState) => ({
  ...state,
  isStatusDrawerOpen: !state.isStatusDrawerOpen,
  selectedCustomStatus: state.isStatusDrawerOpen
    ? undefined
    : state.selectedCustomStatus,
});

const setEditCustomStatus = (
  state: RedactionStatusesState,
  { payload }: { payload: SetEditCustomStatusPayload }
) => ({
  ...state,
  isStatusDrawerOpen: true,
  selectedCustomStatus: payload.customStatus,
});

const clearEditCustomStatus = (state: RedactionStatusesState) => ({
  ...state,
  selectedCustomStatus: undefined,
});

export const redactionStatusesReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(FETCH_REDACTION_STATUSES, fetchRedactionStatuses)
      .addCase(FETCH_REDACTION_STATUSES_SUCCESS, fetchRedactionStatusesSuccess)
      .addCase(FETCH_REDACTION_STATUSES_FAILURE, fetchRedactionStatusesFailure)
      .addCase(SAVE_REDACTION_STATUSES_REQUEST, saveRedactionStatusesRequest)
      .addCase(SAVE_REDACTION_STATUSES_SUCCESS, saveRedactionStatusesSuccess)
      .addCase(SAVE_REDACTION_STATUSES_FAILURE, saveRedactionStatusesFailure)
      .addCase(SAVE_REDACTION_CUSTOM_STATUS_REQUEST, savingCustomStatus)
      .addCase(SAVE_REDACTION_CUSTOM_STATUS_SUCCESS, saveCustomStatusSuccess)
      .addCase(SAVE_REDACTION_CUSTOM_STATUS_FAILURE, customStatusOpFailure)
      .addCase(DELETE_REDACTION_CUSTOM_STATUS_REQUEST, savingCustomStatus)
      .addCase(DELETE_REDACTION_CUSTOM_STATUS_SUCCESS, customStatusOpSuccess)
      .addCase(DELETE_REDACTION_CUSTOM_STATUS_FAILURE, customStatusOpFailure)
      .addCase(RESTORE_REDACTION_CUSTOM_STATUS_REQUEST, savingCustomStatus)
      .addCase(RESTORE_REDACTION_CUSTOM_STATUS_SUCCESS, customStatusOpSuccess)
      .addCase(RESTORE_REDACTION_CUSTOM_STATUS_FAILURE, customStatusOpFailure)
      .addCase(
        PERMANENTLY_DELETE_REDACTION_CUSTOM_STATUS_REQUEST,
        savingCustomStatus
      )
      .addCase(
        REORDER_REDACTION_CUSTOM_STATUSES_REQUEST,
        reorderStatusesRequest
      )
      .addCase(REORDER_REDACTION_CUSTOM_STATUSES_SUCCESS, customStatusOpSuccess)
      .addCase(REORDER_REDACTION_CUSTOM_STATUSES_FAILURE, customStatusOpFailure)
      .addCase(TOGGLE_STATUS_DRAWER, toggleStatusDrawer)
      .addCase(SET_EDIT_CUSTOM_STATUS, setEditCustomStatus)
      .addCase(CLEAR_EDIT_CUSTOM_STATUS, clearEditCustomStatus);
  }),
};
